import { ClipboardUnavailableError } from '../clipboard.js';
import { InboxDeclinedError, PeerConnectionFailedError, SignalingConnectionError } from '../errors.js';
import { InboxCreationError, UnknownInboxKeyError } from '../inbox-api.js';
import { t } from '../i18n/index.js';
import { ShareUnavailableError } from '../native-share.js';
import { CameraError } from '../qr-scanner.js';
import { generateStars, starsToBoxShadow } from '../starfield.js';
import { el } from './dom.js';

const STAR_LAYERS: readonly Readonly<{ count: number; className: string; seed: number }>[] = [
  { count: 220, className: 'stars stars--small', seed: 7 },
  { count: 70, className: 'stars stars--medium', seed: 42 },
  { count: 25, className: 'stars stars--large', seed: 1337 },
];

export const mountStarfield = (root: HTMLElement): void => {
  const sky = el('div', { className: 'sky' });
  sky.setAttribute('aria-hidden', 'true');
  for (const layer of STAR_LAYERS) {
    const stars = el('div', { className: layer.className });
    stars.style.boxShadow = starsToBoxShadow(generateStars(layer.count, 2000, 2000, layer.seed));
    sky.append(stars);
  }
  sky.append(el('div', { className: 'nebula nebula--violet' }), el('div', { className: 'nebula nebula--cyan' }));
  root.prepend(sky);
};

export const createHeader = (tagline: string): HTMLElement =>
  el('header', { className: 'header' }, [
    el('a', { className: 'brand', href: '/' }, [
      el('span', { className: 'brand__orb' }),
      el('span', { className: 'brand__name', text: 'Sync Port' }),
    ]),
    el('p', { className: 'tagline', text: tagline }),
    el('div', { className: 'secure', title: t('secure.tooltip') }, [
      el('span', { className: 'secure__lock', text: '🔒' }),
      el('span', { text: t('secure.badge') }),
    ]),
  ]);

export const createErrorCard = (message: string, actionLabel: string, onAction: () => void): HTMLElement => {
  const button = el('button', { className: 'button button--ghost', type: 'button', text: actionLabel });
  button.addEventListener('click', onAction);
  return el('section', { className: 'card card--error' }, [
    el('div', { className: 'card__title', text: t('error.title') }),
    el('p', { className: 'card__text', text: message }),
    button,
  ]);
};

/** Full-width link back to the start page so a new transfer can begin. */
export const createRestartButton = (label: string): HTMLElement =>
  el('a', { className: 'button button--large', href: '/', text: label });

export const errorText = (error: unknown): string => (error instanceof Error ? error.message : String(error));

/** Translates failures the user can act on; anything else keeps its diagnostic text. */
export const userFacingError = (error: unknown): string => {
  if (error instanceof SignalingConnectionError) {
    switch (error.failure) {
      case 'room-not-found':
        return t('error.roomNotFound');
      case 'room-full':
        return t('error.roomFull');
      case 'inbox-not-found':
        return t('error.inboxNotFound');
      case 'inbox-unauthorized':
        return t('error.inboxUnauthorized');
      case 'unreachable':
        return t('error.signaling');
      case 'other':
        return error.message;
    }
  }
  if (error instanceof InboxDeclinedError) {
    return t('error.declined');
  }
  if (error instanceof UnknownInboxKeyError) {
    return t('error.keyUnknown');
  }
  if (error instanceof InboxCreationError) {
    return error.reason === 'limit' ? t('error.inboxLimit') : error.message;
  }
  if (error instanceof PeerConnectionFailedError) {
    return t('error.peerFailed');
  }
  if (error instanceof ClipboardUnavailableError) {
    return t('clipboard.blocked');
  }
  if (error instanceof ShareUnavailableError) {
    return t('share.unavailable');
  }
  if (error instanceof CameraError) {
    switch (error.failure) {
      case 'denied':
        return t('camera.denied');
      case 'not-found':
        return t('camera.notFound');
      case 'busy':
        return t('camera.busy');
      case 'other':
        return t('camera.failed', { reason: error.reason });
    }
  }
  return errorText(error);
};
